import { useLocation, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { OGTags } from "@/components/OGTags";
import { ProductCard } from "@/components/ProductCard";
import { ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchProducts, ShopifyProduct } from "@/lib/shopify";
import collectionLiving from "@/assets/collection-living.webp";
import collectionBedroom from "@/assets/collection-bedroom.webp";
import collectionDining from "@/assets/collection-dining.webp";
import collectionOutdoor from "@/assets/collection-outdoor.webp";

const rooms = [
  { label: "Living Room", href: "/category/living", image: collectionLiving },
  { label: "Bedroom", href: "/category/bedroom", image: collectionBedroom },
  { label: "Dining", href: "/category/dining", image: collectionDining },
  { label: "Outdoor", href: "/category/outdoor", image: collectionOutdoor },
];

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState<ShopifyProduct[]>([]);

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
    // Load some best sellers to show (same ranking as the Best Sellers page)
    fetchProducts(8, undefined, { sortKey: "BEST_SELLING" }).then(setProducts).catch(() => {});
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/categories?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <OGTags
        title="Page not found | LoftLoom"
        description="The page you're looking for doesn't exist. Browse best sellers, shop by room, or search LoftLoom's modern furniture catalog."
        url="https://loftloom.com/404"
        noindex
      />
      <AnnouncementBar />
      <Header />
      <main className="flex-1">
        {/* Hero 404 */}
        <section className="bg-foreground text-primary-foreground py-16 md:py-24 text-center">
          <div className="container">
            <p className="text-[11px] tracking-[0.25em] uppercase text-primary-foreground/50 mb-3 font-medium">Error 404</p>
            <h1 className="font-display text-[3rem] md:text-[5rem] font-semibold leading-[0.9] tracking-tight mb-4">
              Page Not Found
            </h1>
            <p className="text-primary-foreground/60 text-base max-w-md mx-auto mb-8">
              The page you're looking for doesn't exist or has been moved. Let us help you find what you need.
            </p>

            {/* Search box */}
            <form onSubmit={handleSearch} className="max-w-md mx-auto mb-8">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for products..."
                    className="pl-10 h-12 bg-background text-foreground rounded-none"
                  />
                </div>
                <Button type="submit" size="lg" className="rounded-none h-12 px-6">
                  Search
                </Button>
              </div>
            </form>

            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg" className="bg-primary-foreground text-foreground hover:bg-primary-foreground/90 text-[12px] tracking-widest uppercase px-8 py-6 rounded-none font-medium">
                <Link to="/">
                  Back to Home
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-foreground text-[12px] tracking-widest uppercase px-8 py-6 rounded-none font-medium">
                <Link to="/best-sellers">Shop Best Sellers</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Best Sellers */}
        {products.length > 0 && (
          <section className="py-12 md:py-16 border-b border-border">
            <div className="container">
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-8 text-center">
                Popular Right Now
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {products.slice(0, 4).map((product) => (
                  <ProductCard key={product.node.id} product={product} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Shop by Room */}
        <section className="py-12 md:py-16">
          <div className="container">
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-8 text-center">Shop by Room</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {rooms.map((room) => (
                <Link key={room.label} to={room.href} className="group">
                  <div className="aspect-[4/3] overflow-hidden mb-2">
                    <img
                      src={room.image}
                      alt={room.label}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                  <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{room.label}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Quick links */}
        <section className="py-10 border-t border-border">
          <div className="container text-center">
            <h3 className="text-lg font-semibold text-foreground mb-4">Popular Categories</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: "Sofas", slug: "sofas" },
                { label: "Rugs", slug: "area-rugs" },
                { label: "Decor", slug: "decor" },
                { label: "New Arrivals", slug: "new-arrivals" },
                { label: "Best Sellers", slug: "best-sellers" },
              ].map(({ label: cat, slug }) => (
                <Link
                  key={cat}
                  to={`/category/${slug}`}
                  className="px-5 py-2 border border-border text-sm text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
                >
                  {cat}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;
